// 화면 그리기(§9.8) — 기하 계산(적중 판정·경계 상자·이동·점 줄이기·화살촉). DOM 없음(시험 대상).
//
// 🔴 판정은 **픽셀 공간**에서 한다. 문서 좌표는 가로·세로가 각각 0~1 이라 16:9 화면에서 x 0.01 과 y 0.01 은
//    실제 거리가 다르다 — 비율 좌표로 거리를 재면 가로 선은 잘 잡히고 세로 선은 안 잡힌다.
import { REF_HEIGHT } from './model.ts';
import type { DrawObj, ObjPatch, ShapeObj, StrokeObj, TextObj } from './model.ts';

/** 그리는 영역의 실제 크기(px). */
export interface Space {
  w: number;
  h: number;
}

/** 비율 좌표 한 점. */
export type Pt = [number, number];

/** 기준 높이 px → 실제 px(선 굵기·글자 크기). */
export function scalePx(v: number, s: Space): number {
  return (v * s.h) / REF_HEIGHT;
}

/** 글자 한 줄 높이 배수(렌더와 적중 판정이 같은 값을 쓴다). */
export const TEXT_LINE_HEIGHT = 1.25;
/**
 * 글자 폭 어림(글자 크기 배수) — 적중 판정·경계 상자용. 한글은 글자 크기와 거의 같고 영문은 그 절반쯤이다.
 * 캔버스 `measureText` 가 있으면 렌더 쪽이 정확한 값을 쓰고, DOM 없는 곳(시험·판정)은 이 어림을 쓴다.
 */
function approxTextWidth(line: string, size: number): number {
  let w = 0;
  for (const ch of line) w += ch.charCodeAt(0) > 0x2e7f ? size : size * 0.56;
  return w;
}

/** 점과 선분 사이 거리(px). */
export function distToSeg(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

/** 객체의 경계 상자(비율 좌표 [x0, y0, x1, y1]). 글자는 어림 폭. */
export function bbox(o: DrawObj, s: Space): [number, number, number, number] {
  switch (o.kind) {
    case 'pen':
    case 'marker': {
      let x0 = 1, y0 = 1, x1 = 0, y1 = 0;
      for (let i = 0; i < o.pts.length; i += 2) {
        x0 = Math.min(x0, o.pts[i]);
        x1 = Math.max(x1, o.pts[i]);
        y0 = Math.min(y0, o.pts[i + 1]);
        y1 = Math.max(y1, o.pts[i + 1]);
      }
      return o.pts.length ? [x0, y0, x1, y1] : [0, 0, 0, 0];
    }
    case 'text': {
      const size = scalePx(o.width, s);
      const lines = o.text.split('\n');
      const wPx = Math.max(...lines.map((l) => approxTextWidth(l, size)));
      const hPx = lines.length * size * TEXT_LINE_HEIGHT;
      return [o.at[0], o.at[1], o.at[0] + wPx / s.w, o.at[1] + hPx / s.h];
    }
    default:
      return [Math.min(o.a[0], o.b[0]), Math.min(o.a[1], o.b[1]), Math.max(o.a[0], o.b[0]), Math.max(o.a[1], o.b[1])];
  }
}

/**
 * 한 점(비율 좌표)이 객체를 건드리는가 — 지우개·선택이 쓴다.
 * @param tolPx 손떨림 여유(px). 선 굵기의 절반이 더해진다(굵은 선은 가장자리를 눌러도 잡힌다).
 */
export function hitObj(o: DrawObj, p: Pt, s: Space, tolPx: number): boolean {
  const px = p[0] * s.w;
  const py = p[1] * s.h;
  const tol = tolPx + scalePx(o.width, s) / 2;
  switch (o.kind) {
    case 'pen':
    case 'marker':
      return hitStroke(o, px, py, s, tol);
    case 'line':
    case 'arrow':
      return distToSeg(px, py, o.a[0] * s.w, o.a[1] * s.h, o.b[0] * s.w, o.b[1] * s.h) <= tol;
    case 'rect':
      return hitRect(o, px, py, s, tol);
    case 'ellipse':
      return hitEllipse(o, px, py, s, tol);
    case 'text': {
      const [x0, y0, x1, y1] = bbox(o, s);
      return px >= x0 * s.w - tolPx && px <= x1 * s.w + tolPx && py >= y0 * s.h - tolPx && py <= y1 * s.h + tolPx;
    }
  }
}

function hitStroke(o: StrokeObj, px: number, py: number, s: Space, tol: number): boolean {
  const n = o.pts.length / 2;
  if (n === 1) {
    return Math.hypot(px - o.pts[0] * s.w, py - o.pts[1] * s.h) <= tol;
  }
  for (let i = 0; i + 3 < o.pts.length; i += 2) {
    if (distToSeg(px, py, o.pts[i] * s.w, o.pts[i + 1] * s.h, o.pts[i + 2] * s.w, o.pts[i + 3] * s.h) <= tol) {
      return true;
    }
  }
  return false;
}

/** 사각형: 채웠으면 안쪽 전체, 아니면 테두리 근처만(안쪽의 다른 그림을 고를 수 있게). */
function hitRect(o: ShapeObj, px: number, py: number, s: Space, tol: number): boolean {
  const x0 = Math.min(o.a[0], o.b[0]) * s.w;
  const x1 = Math.max(o.a[0], o.b[0]) * s.w;
  const y0 = Math.min(o.a[1], o.b[1]) * s.h;
  const y1 = Math.max(o.a[1], o.b[1]) * s.h;
  const inOuter = px >= x0 - tol && px <= x1 + tol && py >= y0 - tol && py <= y1 + tol;
  if (!inOuter) return false;
  if (o.fill) return true;
  const inInner = px > x0 + tol && px < x1 - tol && py > y0 + tol && py < y1 - tol;
  return !inInner;
}

/** 원(타원): 중심 기준 정규화 반지름으로 판정 — 테두리 폭 = 여유를 반지름 비율로 바꾼 값. */
function hitEllipse(o: ShapeObj, px: number, py: number, s: Space, tol: number): boolean {
  const cx = ((o.a[0] + o.b[0]) / 2) * s.w;
  const cy = ((o.a[1] + o.b[1]) / 2) * s.h;
  const rx = Math.max(1, (Math.abs(o.a[0] - o.b[0]) / 2) * s.w);
  const ry = Math.max(1, (Math.abs(o.a[1] - o.b[1]) / 2) * s.h);
  const d = Math.hypot((px - cx) / rx, (py - cy) / ry); // 1 = 테두리
  const band = tol / Math.min(rx, ry);
  return o.fill ? d <= 1 + band : Math.abs(d - 1) <= band;
}

/** 이동 — 새 위치를 patch 로(모델의 `upd`). 화면 밖으로 나가지 않게 이동량을 자른다. */
export function movePatch(o: DrawObj, dx: number, dy: number, s: Space): ObjPatch {
  const [x0, y0, x1, y1] = bbox(o, s);
  const cdx = Math.max(-x0, Math.min(1 - x1, dx));
  const cdy = Math.max(-y0, Math.min(1 - y1, dy));
  const mv = (p: Pt): [number, number] => [clamp01(p[0] + cdx), clamp01(p[1] + cdy)];
  switch (o.kind) {
    case 'pen':
    case 'marker':
      return { pts: o.pts.map((v, i) => clamp01(v + (i % 2 === 0 ? cdx : cdy))) };
    case 'text':
      return { at: mv((o as TextObj).at) };
    default:
      return { a: mv(o.a), b: mv(o.b) };
  }
}

/** 붙여 넣은 복제본의 위치 — 원본에서 조금 비켜 둔다(겹쳐서 복제된 줄 모르는 일이 없게). */
export const DUPLICATE_OFFSET_PX = 16;

function clamp01(v: number): number {
  return Math.round(Math.min(1, Math.max(0, v)) * 10000) / 10000;
}

/**
 * 획 점 줄이기 — 직전 점과 `minPx` 보다 가까운 점은 버린다(마우스는 1px 마다 이벤트를 내 획 하나가 수천 점이 된다).
 * @returns 이 점을 남길지. `last` = 마지막으로 남긴 점(없으면 첫 점이라 남긴다).
 */
export function keepPoint(last: Pt | null, p: Pt, s: Space, minPx: number): boolean {
  if (!last) return true;
  return Math.hypot((p[0] - last[0]) * s.w, (p[1] - last[1]) * s.h) >= minPx;
}

/** 화살촉 두 날개 끝(px 좌표). 촉 길이 = 굵기에 비례(가는 선도 보이게 하한). */
export function arrowWings(ax: number, ay: number, bx: number, by: number, widthPx: number): [Pt, Pt] {
  const len = Math.max(12, widthPx * 4);
  const ang = Math.atan2(by - ay, bx - ax);
  const spread = Math.PI / 7;
  return [
    [bx - len * Math.cos(ang - spread), by - len * Math.sin(ang - spread)],
    [bx - len * Math.cos(ang + spread), by - len * Math.sin(ang + spread)],
  ];
}
